import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Loader } from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./whatsapp-dashboard.scss";

/**
 * /whatsapp/connect/relay — the one URL registered with Meta.
 *
 * Meta matches `redirect_uri` **exactly** (Strict Mode is on, and there is no
 * wildcard), so every institute has to return to the same address or somebody
 * has to add their domain to the app console by hand — one entry per subdomain
 * and per custom domain, forever, failing silently when it is missed.
 *
 * So every institute returns *here*, and this page sends the browser back where
 * it came from. It is deliberately the thinnest possible page:
 *
 *   • it never exchanges the code — that needs a session, and this page has none
 *   • it never decides the return origin — the backend signed that into `state`
 *   • it renders nothing on the happy path, because it is gone in a moment
 *
 * **Public by necessity.** It runs on the canonical host, in the popup Meta
 * redirected, where the institute admin has no session — their token lives in
 * `localStorage` on their *own* origin. Wrapping this route in `ProtectedRoute`
 * would bounce the popup to a login screen on the wrong domain, which is the
 * exact failure it exists to prevent.
 */
const WhatsAppConnectRelay = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const bouncedRef = useRef(false);

  useEffect(() => {
    // Meta can re-render this route; the redirect must fire exactly once.
    if (bouncedRef.current) return;
    bouncedRef.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state") || "";
    const oauthError = searchParams.get("error");

    /**
     * A denial is a completed flow, not a broken one. Forwarding it home means
     * the institute sees "you cancelled" on their own screen instead of a bare
     * error on a domain they do not recognise.
     */
    if (oauthError) {
      const description =
        searchParams.get("error_description") ||
        searchParams.get("error_reason") ||
        "Authorization was denied.";
      setError(description);
      return;
    }

    if (!code) {
      setError("No authorization code was returned by Meta.");
      return;
    }

    (async () => {
      /**
       * Two cases resolve to "already home", and both must skip the lookup.
       *
       * `state=platform` is the tenant-admin flow, which runs on this host
       * already — handled here so the SA connect keeps working exactly as it did
       * before this page existed.
       *
       * An **empty** state means the backend saw no reason to mint one: the
       * dialog was returning to the origin it left. That is the institute whose
       * LMS is served from the canonical host itself. Treating a missing state
       * as an error would break precisely the customer for whom the relay was
       * never needed.
       */
      if (!state || state === "platform") {
        forward(window.location.origin, code, state);
        return;
      }

      try {
        const { data } = await apiClient.post("/whatsapp/connect/resolve-state", { state });
        if (!data?.origin) {
          setError("This connect link is not valid. Close this window and try again.");
          return;
        }
        forward(data.origin, code, state);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Could not complete the connection. Close this window and try again."
        );
      }
    })();
  }, [searchParams]);

  /**
   * `replace`, not `assign` — the authorization code is in this URL, and a
   * history entry on the canonical host would keep it reachable by Back long
   * after it was spent.
   */
  const forward = (origin, code, state) => {
    const url = new URL("/whatsapp/connect/callback", origin);
    url.searchParams.set("code", code);
    if (state) url.searchParams.set("state", state);
    window.location.replace(url.toString());
  };

  return (
    <div className="wa-connect-relay">
      <div className="wa-connect-empty">
        {error ? (
          <>
            <div className="wa-connect-empty__icon wa-connect-empty__icon--error">
              <AlertCircle size={40} />
            </div>
            <h3>Connection failed</h3>
            <p className="wa-text-muted">{error}</p>
            <p className="wa-text-muted">You can close this window.</p>
          </>
        ) : (
          <>
            <div className="wa-connect-empty__icon wa-connect-empty__icon--loading">
              <Loader size={40} className="wa-spin" />
            </div>
            <h3>Finishing up…</h3>
            <p className="wa-text-muted">Returning you to your dashboard.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppConnectRelay;
